'use client'

import { type FC } from 'react'
import { FormattedMessage } from 'react-intl'

import { useRecommendationIntersection } from '../../../../cross-sell'
import {
  gtmDataLayerPushRecommendationTracking,
  RECOMMENDATION_SERVICE_PDP_FIRST_IMPRESSION,
  RECOMMENDATION_WIDGET_FIRST_IMPRESSION,
  RecommendationHorizontalSlider,
  RecommendationWidgetContainer,
  RecommendationWidgetMoreLink,
  RecommendationWidgetProvider,
  RecommendationWidgetTitle,
  useGetRecommendationWidgetId,
  useSwrFetchRecommendationService,
} from '../../../../recommendation'
import { productAccessorStockStatusIsAvailable } from '../../../model/productAccessor/productAccessorStockStatusIsAvailable'
import { useProductPageContext } from '../../productPageContext'

import { useShouldRenderProductRecommendationWidgetFirstImpressionSlider } from './useShouldRenderProductRecommendationAreaFirstImpressionSlider'

export const ProductRecommendationWidgetFirstImpressionSlider: FC = () => {
  const { currentOffer, product } = useProductPageContext()

  const available = productAccessorStockStatusIsAvailable(product)
  const shouldRender =
    useShouldRenderProductRecommendationWidgetFirstImpressionSlider()
  const { handleIntersection, intersected } = useRecommendationIntersection()
  const sellerId = currentOffer?.seller?.sellerId ?? ''

  const serviceId = RECOMMENDATION_SERVICE_PDP_FIRST_IMPRESSION
  const widgetId = useGetRecommendationWidgetId(
    serviceId,
    RECOMMENDATION_WIDGET_FIRST_IMPRESSION,
  )

  const { data, error } = useSwrFetchRecommendationService(
    intersected && shouldRender && available
      ? {
          pid: product.upid,
          sellerid: sellerId,
          serviceId,
        }
      : undefined,
  )

  if (
    error ||
    !shouldRender ||
    /*
     * Similar products will be displayed on top when there are no variants available in stock
     * https://jira.shop-apotheke.com/browse/WSHOME-734
     */
    !available ||
    !widgetId ||
    data?.recommendations[widgetId]?.content.length === 0
  ) {
    return null
  }

  const widgetData = data?.recommendations[widgetId]

  return (
    <RecommendationWidgetProvider
      serviceId={serviceId}
      widgetData={widgetData}
      widgetId={widgetId}
    >
      <RecommendationWidgetContainer
        className="mb-7 hidden tablet:block"
        onIntersect={handleIntersection}
        widgetData={data}
        widgetId={widgetId}
      >
        <RecommendationWidgetTitle className="text-m font-bold" />
        <RecommendationWidgetMoreLink>
          <FormattedMessage id="general.label.ShowAll" />
        </RecommendationWidgetMoreLink>
        <RecommendationHorizontalSlider
          // eslint-disable-next-line tailwindcss/no-arbitrary-value -- don't reference, refactor if touched
          className="[--name-line-clamp:1]"
          onGtmDataLayerPush={(widgetProductId, trackingToken) => {
            gtmDataLayerPushRecommendationTracking({
              productId: product.upid,
              trackingToken,
              widgetId,
              widgetProductId,
            })
          }}
          products={widgetData?.content}
          productsPerPage={6}
        />
      </RecommendationWidgetContainer>
    </RecommendationWidgetProvider>
  )
}
